import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * PaymentThankYou
 * ───────────────
 * Standalone post-payment landing page. Shown to users after Stripe Checkout
 * redirects back here (success_url in payments_routes.py).
 *
 * Auto-redirects to /dashboard after 5 seconds. User can also click
 * "Go to my account" to skip the wait.
 *
 * Read-only — does NOT activate the subscription. That happens in the backend
 * via the Stripe webhook handler. By the time the user lands here, their
 * subscription is already activated (or activating in the next ~1 second).
 */
const PaymentThankYou = ({ isDarkMode = true }) => {
  const navigate = useNavigate();
  const [secondsLeft, setSecondsLeft] = useState(5);

  const dest = '/profile?tab=membership&payment=success';

  useEffect(() => {
    const interval = setInterval(() => {
      setSecondsLeft((s) => (s > 0 ? s - 1 : 0));
    }, 1000);
    const timeout = setTimeout(() => navigate(dest, { replace: true }), 5000);
    return () => {
      clearInterval(interval);
      clearTimeout(timeout);
    };
  }, [navigate]);

  const goNow = () => navigate(dest, { replace: true });

  return (
    <div className={`min-h-screen flex flex-col items-center justify-center px-5 py-12 ${
      isDarkMode ? 'bg-slate-950 text-slate-100' : 'bg-gradient-to-br from-blue-50 via-white to-purple-50 text-slate-900'
    }`}>
      <div className={`w-full max-w-md rounded-3xl shadow-2xl p-8 text-center ${
        isDarkMode ? 'bg-slate-900 border border-slate-800' : 'bg-white border border-slate-200'
      }`}>
        {/* Animated check circle */}
        <div className="mx-auto w-20 h-20 rounded-full bg-gradient-to-br from-emerald-400 to-emerald-600 flex items-center justify-center shadow-lg mb-6">
          <svg className="w-12 h-12 text-white" fill="none" stroke="currentColor" strokeWidth="3" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" d="M5 13l4 4L19 7" />
          </svg>
        </div>

        <h1 className={`text-3xl font-extrabold tracking-tight mb-3 ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>
          Thank you!
        </h1>
        <p className={`text-base mb-1 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          Your payment was successful.
        </p>
        <p className={`text-base mb-8 ${isDarkMode ? 'text-slate-300' : 'text-slate-600'}`}>
          Welcome to <span className="font-bold bg-gradient-to-r from-blue-500 to-purple-600 bg-clip-text text-transparent">DarijaPro Pro</span>.
        </p>

        <button
          onClick={goNow}
          className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-500 to-purple-600 text-white font-semibold shadow-lg hover:opacity-95 transition-all"
        >
          Go to my account
        </button>

        <p className={`text-xs mt-5 ${isDarkMode ? 'text-slate-500' : 'text-slate-400'}`}>
          Redirecting automatically in {secondsLeft}s…
        </p>
      </div>

      <a href="/" className={`mt-6 text-sm ${isDarkMode ? 'text-slate-400 hover:text-slate-200' : 'text-slate-600 hover:text-slate-900'}`}>
        ← Back to home
      </a>
    </div>
  );
};

export default PaymentThankYou;
